// PersonalizedRecommendation Component - AI-powered personalized place recommendations
// based on GoodWork Crafting data (Valou Moods, Mental Load, Personality, etc.)

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Sparkles,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Heart,
  Battery,
  Brain,
  RefreshCw,
  Wand2,
  MapPin,
  Download,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import {
  hasCraftingProfile,
  getImportableData,
  toCraftingData,
  generatePersonalizedRecommendation,
  type ImportableData,
} from '../../services/craftingIntegrationService';
import { updateUserProfile } from '../../services/userSearchService';
import {
  generateAIRecommendation,
  isAIAvailable,
  type AIRecommendation,
} from '../../services/geminiService';
import { getUserProfile } from '../../services/userSearchService';
import { LocationAutocomplete } from '../location/LocationAutocomplete';
import { filterPlacesByRadius, sortPlacesByDistance } from '../../utils/distance';
import { CraftingTeaser } from '../integration/CraftingTeaser';
import type { Place, GeocodedLocation } from '../../types';

interface PersonalizedRecommendationProps {
  places: Place[];
  onPlaceClick: (place: Place) => void;
}

// Priority to mood color mapping
const PRIORITY_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  relax: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  energize: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  focus: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  social: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
  explore: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200' },
};

const PRIORITY_ICONS: Record<string, React.ReactNode> = {
  relax: <Battery className="w-5 h-5" />,
  energize: <Sparkles className="w-5 h-5" />,
  focus: <Brain className="w-5 h-5" />,
  social: <Heart className="w-5 h-5" />,
  explore: <Sparkles className="w-5 h-5" />,
};

// LocalStorage key for collapse state
const COLLAPSE_KEY = 'goodplace-recommendation-collapsed';

// Radius options for location filter
const RADIUS_OPTIONS = [
  { value: 5, label: '5 km' },
  { value: 10, label: '10 km' },
  { value: 25, label: '25 km' },
  { value: 50, label: '50 km' },
];

export function PersonalizedRecommendation({ places, onPlaceClick }: PersonalizedRecommendationProps) {
  const { user } = useAuth();
  const [craftingData, setCraftingData] = useState<ImportableData | null>(null);
  const [isLinked, setIsLinked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [hasCraftingData, setHasCraftingData] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  // Collapse state with localStorage persistence (default: collapsed)
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(COLLAPSE_KEY);
      // Default to collapsed if no preference stored
      return stored === null ? true : stored === 'true';
    }
    return true;
  });

  // AI recommendation state
  const [aiRecommendation, setAiRecommendation] = useState<AIRecommendation | null>(null);
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [aiError, setAiError] = useState(false);

  // Location filter state
  const [locationFilter, setLocationFilter] = useState<{
    location: GeocodedLocation | null;
    locationInput: string;
    radius: number;
  }>({
    location: null,
    locationInput: '',
    radius: 10,
  });
  const [isLocating, setIsLocating] = useState(false);

  // Get user's current location
  const handleNearbyClick = useCallback(() => {
    if (!navigator.geolocation) {
      console.error('Geolocation not supported');
      return;
    }

    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        // Create a GeocodedLocation from coordinates
        const location: GeocodedLocation = {
          latitude,
          longitude,
          formattedAddress: 'Mein Standort',
          city: '',
          district: '',
          postalCode: '',
          placeId: 'current-location',
        };

        setLocationFilter({
          location,
          locationInput: 'Mein Standort',
          radius: 10,
        });
        setIsLocating(false);

        // Reset AI recommendation when location changes
        setAiRecommendation(null);
      },
      (error) => {
        console.error('Geolocation error:', error);
        setIsLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  // Toggle collapse
  const toggleCollapse = useCallback(() => {
    setIsCollapsed(prev => {
      const newValue = !prev;
      localStorage.setItem(COLLAPSE_KEY, String(newValue));
      return newValue;
    });
  }, []);

  // Load AI recommendation
  const loadAIRecommendation = useCallback(async (data: ImportableData, placesData: Place[]) => {
    if (!isAIAvailable()) {
      setAiError(true);
      return;
    }

    setIsLoadingAI(true);
    setAiError(false);

    try {
      const recommendation = await generateAIRecommendation(
        data,
        placesData,
        user?.displayName || undefined
      );

      if (recommendation) {
        setAiRecommendation(recommendation);
      } else {
        setAiError(true);
      }
    } catch (error) {
      console.error('Error loading AI recommendation:', error);
      setAiError(true);
    } finally {
      setIsLoadingAI(false);
    }
  }, [user?.displayName]);

  // Load data
  const loadData = useCallback(async (showRefreshing = false) => {
    if (!user) {
      setLoading(false);
      return;
    }

    if (showRefreshing) setRefreshing(true);

    try {
      // Check if user has linked Crafting data
      const profile = await getUserProfile(user.uid);
      if (!profile?.craftingLinked) {
        setIsLinked(false);

        // Check if user has Crafting data (even if not linked)
        const hasCrafting = await hasCraftingProfile(user.uid);
        setHasCraftingData(hasCrafting);

        if (hasCrafting) {
          const data = await getImportableData(user.uid);
          setCraftingData(data);
        }

        setLoading(false);
        setRefreshing(false);
        return;
      }

      setIsLinked(true);
      setHasCraftingData(true);

      // Load fresh data from Crafting
      const data = await getImportableData(user.uid);
      setCraftingData(data);

      // AI recommendation wird nur auf Anfrage geladen (nicht automatisch)
    } catch (error) {
      console.error('Error loading crafting data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user, places, loadAIRecommendation]);

  useEffect(() => {
    loadData();
  }, [user]);

  // Filter places by location if set
  const locationFilteredPlaces = useMemo(() => {
    if (!locationFilter.location) {
      return places;
    }

    const center = {
      latitude: locationFilter.location.latitude,
      longitude: locationFilter.location.longitude,
    };

    // Filter by radius and sort by distance
    const filtered = filterPlacesByRadius(places, center, locationFilter.radius);
    return sortPlacesByDistance(filtered, center);
  }, [places, locationFilter.location, locationFilter.radius]);

  // AI Empfehlung nur auf Anfrage laden (wenn User aufklappt oder Button klickt)
  const handleRequestAI = useCallback(() => {
    if (craftingData && locationFilteredPlaces.length > 0 && !isLoadingAI) {
      loadAIRecommendation(craftingData, locationFilteredPlaces);
    }
  }, [craftingData, locationFilteredPlaces, isLoadingAI, loadAIRecommendation]);

  const handleRefresh = () => {
    setAiRecommendation(null);
    if (craftingData && locationFilteredPlaces.length > 0) {
      loadAIRecommendation(craftingData, locationFilteredPlaces);
    }
  };

  const handleQuickImport = async () => {
    if (!user || !craftingData) return;

    setIsImporting(true);
    try {
      const storedData = toCraftingData(craftingData, {
        personality: true,
        motivation: true,
        riasec: true,
        valou: true,
      });

      await updateUserProfile(user.uid, {
        craftingLinked: true,
        craftingData: storedData,
      });

      setIsLinked(true);
    } catch (error) {
      console.error('Error importing crafting data:', error);
    } finally {
      setIsImporting(false);
    }
  };

  // Don't show if not logged in, loading, or no linked data
  if (!user || loading) {
    return null;
  }

  // User hat Crafting-Daten aber noch nicht verknuepft -> Smart Banner
  if (!isLinked && hasCraftingData && craftingData) {
    return (
      <div className="mb-6 px-4">
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-5">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-indigo-100 rounded-full flex-shrink-0">
              <Sparkles className="w-6 h-6 text-indigo-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-indigo-900 text-lg">
                Crafting-Profil erkannt!
              </h3>
              <p className="text-indigo-700 mt-1 text-sm">
                Du hast bereits ein GoodWork Crafting Profil. Verknuepfe es fuer
                personalisierte Empfehlungen basierend auf deinem Wohlbefinden.
              </p>

              {/* Daten-Preview */}
              <div className="flex flex-wrap gap-2 mt-3">
                {craftingData.personality && (
                  <span className="px-2 py-1 bg-white/60 text-indigo-600 text-xs rounded-full">
                    Persoenlichkeit
                  </span>
                )}
                {craftingData.valouMoods && (
                  <span className="px-2 py-1 bg-white/60 text-indigo-600 text-xs rounded-full">
                    Valou Moods
                  </span>
                )}
                {craftingData.averageMentalLoad !== null && (
                  <span className="px-2 py-1 bg-white/60 text-indigo-600 text-xs rounded-full">
                    Mental Load
                  </span>
                )}
              </div>

              <button
                onClick={handleQuickImport}
                disabled={isImporting}
                className="mt-4 px-4 py-2 bg-indigo-500 text-white rounded-lg font-medium
                         hover:bg-indigo-600 transition flex items-center gap-2"
              >
                {isImporting ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                Jetzt verknuepfen
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // User hat keine Crafting-Daten -> Standard Teaser
  if (!isLinked || !craftingData) {
    return (
      <div className="mb-4 px-4">
        <CraftingTeaser variant="inline" showDismiss={false} />
      </div>
    );
  }

  // Get fallback recommendation
  const fallbackRecommendation = generatePersonalizedRecommendation(craftingData);
  const colors = PRIORITY_COLORS[fallbackRecommendation.priority] || PRIORITY_COLORS.explore;
  const icon = PRIORITY_ICONS[fallbackRecommendation.priority];

  // Get places to display (from AI or fallback)
  let displayPlaces: Array<{ place: Place; reason?: string }> = [];

  if (aiRecommendation && aiRecommendation.places.length > 0) {
    // Use AI-recommended places with reasons
    displayPlaces = aiRecommendation.places
      .map(rec => {
        const place = locationFilteredPlaces.find(p => p.id === rec.placeId);
        return place ? { place, reason: rec.reason } : null;
      })
      .filter((p): p is { place: Place; reason: string } => p !== null);
  }

  // Fallback if AI didn't return valid places
  if (displayPlaces.length === 0) {
    const suggestedPlaces = locationFilteredPlaces
      .filter(place => {
        const placeText = `${place.name} ${place.description || ''} ${place.goodFactor || ''}`.toLowerCase();
        return fallbackRecommendation.suggestedCategories.some(cat =>
          placeText.includes(cat.toLowerCase().split(' ')[0])
        );
      })
      .slice(0, 3);

    const finalPlaces = suggestedPlaces.length > 0
      ? suggestedPlaces
      : locationFilteredPlaces.filter(p => p.status === 'approved').slice(0, 3);

    displayPlaces = finalPlaces.map(place => ({ place }));
  }

  // Get message (AI or fallback)
  const message = aiRecommendation?.message || fallbackRecommendation.message;

  return (
    <div className={`rounded-xl border ${colors.border} ${colors.bg} overflow-hidden mb-6`}>
      {/* Header - always visible, clickable div instead of nested buttons */}
      <div className="w-full p-4 flex items-center justify-between hover:bg-white/30 transition">
        {/* Left side - clickable to toggle */}
        <div
          onClick={toggleCollapse}
          className="flex items-center gap-3 flex-1 cursor-pointer"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && toggleCollapse()}
        >
          <div className={`p-2 rounded-full ${colors.bg} ${colors.text}`}>
            {isLoadingAI ? (
              <Wand2 className="w-5 h-5 animate-pulse" />
            ) : (
              icon
            )}
          </div>
          <div className="text-left">
            <h3 className={`font-semibold ${colors.text} flex items-center gap-2`}>
              Meine GoodPlace Empfehlung
              {aiRecommendation && !aiError && (
                <span className="text-xs bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded-full font-normal">
                  KI
                </span>
              )}
            </h3>
            <p className="text-sm text-gray-600">
              {isCollapsed ? 'Klicken zum Aufklappen' : 'Basierend auf deinem GoodWork Crafting Profil'}
            </p>
          </div>
        </div>
        {/* Right side - buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleRefresh}
            disabled={refreshing || isLoadingAI}
            className="p-2 hover:bg-white/50 rounded-full transition"
            title="Neue Empfehlung generieren"
          >
            <RefreshCw className={`w-4 h-4 text-gray-500 ${(refreshing || isLoadingAI) ? 'animate-spin' : ''}`} />
          </button>
          <div
            onClick={toggleCollapse}
            className="p-1 cursor-pointer"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && toggleCollapse()}
          >
            {isCollapsed ? (
              <ChevronDown className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronUp className="w-5 h-5 text-gray-500" />
            )}
          </div>
        </div>
      </div>

      {/* Content - collapsible */}
      {!isCollapsed && (
        <div className="px-4 pb-4">
          {/* AI Loading Skeleton */}
          {isLoadingAI && (
            <div className="animate-pulse space-y-3">
              <div className="bg-white/70 rounded-lg p-3">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
              <div className="space-y-2">
                {[1, 2, 3].map(i => (
                  <div key={i} className="flex items-center gap-3 p-2 bg-white rounded-lg">
                    <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded w-1/2 mb-1"></div>
                      <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Location Filter Section */}
          <div className="mb-4 p-3 bg-white/70 rounded-lg border border-gray-200">
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-2">
              Standort filtern
            </p>

            <div className="space-y-3">
              {/* Location Input */}
              <LocationAutocomplete
                value={locationFilter.locationInput}
                onChange={(value) => setLocationFilter(prev => ({ ...prev, locationInput: value }))}
                onSelect={(location) => {
                  setLocationFilter(prev => ({
                    ...prev,
                    location,
                    locationInput: location.formattedAddress,
                  }));
                  // Reset AI recommendation when location changes
                  setAiRecommendation(null);
                }}
                placeholder="Wo moechtest du suchen?"
                className="w-full"
                showNearbyButton={true}
                onNearbyClick={handleNearbyClick}
                isLocating={isLocating}
                nearbyAddress={locationFilter.location?.placeId === 'current-location' ? 'Mein Standort' : undefined}
              />

              {/* Radius Selector - only show when location is selected */}
              {locationFilter.location && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-gray-600">Umkreis:</span>
                  <div className="flex gap-1 flex-wrap">
                    {RADIUS_OPTIONS.map(option => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setLocationFilter(prev => ({ ...prev, radius: option.value }));
                          setAiRecommendation(null); // Reset AI when radius changes
                        }}
                        className={`px-3 py-1 text-sm rounded-full transition ${
                          locationFilter.radius === option.value
                            ? 'bg-indigo-500 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Clear Filter Button */}
              {locationFilter.location && (
                <button
                  onClick={() => {
                    setLocationFilter({ location: null, locationInput: '', radius: 10 });
                    setAiRecommendation(null); // Reset AI when filter cleared
                  }}
                  className="text-sm text-gray-500 hover:text-gray-700 underline"
                >
                  Standort-Filter entfernen
                </button>
              )}

              {/* Info when filtered */}
              {locationFilter.location && (
                <p className="text-xs text-gray-500">
                  <MapPin className="w-3 h-3 inline mr-1" />
                  {locationFilteredPlaces.length} Orte im Umkreis von {locationFilter.radius} km
                  {locationFilter.location.city && ` um ${locationFilter.location.city}`}
                </p>
              )}

              {/* Warning if no places found */}
              {locationFilter.location && locationFilteredPlaces.length === 0 && (
                <p className="text-xs text-orange-600">
                  Keine Orte in diesem Umkreis gefunden. Versuche einen groesseren Radius.
                </p>
              )}
            </div>
          </div>

          {/* Button to request AI recommendation */}
          {!aiRecommendation && !isLoadingAI && (
            <div className="mb-4">
              <button
                onClick={handleRequestAI}
                disabled={locationFilteredPlaces.length === 0}
                className="w-full flex items-center justify-center gap-2 py-3 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Wand2 className="w-5 h-5" />
                KI-Empfehlung generieren
              </button>
              <p className="text-xs text-gray-500 text-center mt-2">
                Basierend auf deinem Mental Load, Wohlbefinden und Valou-Profil
              </p>
            </div>
          )}

          {/* Recommendation Message */}
          {!isLoadingAI && (
            <>
              <div className="bg-white/70 rounded-lg p-3 mb-4">
                <p className={`text-sm ${colors.text} font-medium leading-relaxed`}>
                  {message}
                </p>

                {/* Status badges */}
                <div className="mt-2 flex flex-wrap gap-2">
                  {craftingData.averageMentalLoad !== null && (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      craftingData.averageMentalLoad > 70
                        ? 'bg-red-100 text-red-700'
                        : craftingData.averageMentalLoad > 50
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-green-100 text-green-700'
                    }`}>
                      Mental Load: {craftingData.averageMentalLoad}%
                    </span>
                  )}
                  {craftingData.currentWellbeing !== null && (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      craftingData.currentWellbeing < 5
                        ? 'bg-orange-100 text-orange-700'
                        : craftingData.currentWellbeing < 7
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-green-100 text-green-700'
                    }`}>
                      Wohlbefinden: {craftingData.currentWellbeing}/10
                    </span>
                  )}
                </div>
              </div>

              {/* Suggested Places with Reasons */}
              {displayPlaces.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                    Passende Orte fuer dich
                  </p>
                  <div className="grid gap-2">
                    {displayPlaces.map(({ place, reason }) => (
                      <button
                        key={place.id}
                        onClick={() => onPlaceClick(place)}
                        className="flex items-start gap-3 p-3 bg-white rounded-lg hover:shadow-md transition text-left w-full"
                      >
                        {place.images && place.images.length > 0 ? (
                          <img
                            src={place.images[0]}
                            alt={place.name}
                            className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="w-14 h-14 rounded-lg bg-gray-200 flex items-center justify-center flex-shrink-0">
                            <Sparkles className="w-6 h-6 text-gray-400" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900">{place.name}</p>
                          <p className="text-xs text-gray-500 mb-1">
                            {place.district || place.address}
                          </p>
                          {reason && (
                            <p className="text-xs text-indigo-600 italic leading-snug">
                              {reason}
                            </p>
                          )}
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Suggested Categories as Tags */}
              {fallbackRecommendation.suggestedCategories.length > 0 && !aiRecommendation && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {fallbackRecommendation.suggestedCategories.map((cat, idx) => (
                    <span
                      key={idx}
                      className={`text-xs px-2 py-0.5 rounded-full ${colors.bg} ${colors.text} border ${colors.border}`}
                    >
                      {cat}
                    </span>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default PersonalizedRecommendation;
